import React from 'react';
import { motion } from 'framer-motion';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-16 px-4">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8"
        >
          <motion.h1
            className="text-4xl font-serif font-bold text-gray-900 dark:text-white mb-8"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            Terms of Service
          </motion.h1>

          <motion.div
            className="prose prose-lg dark:prose-invert max-w-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              <em>Last updated: {new Date().toLocaleDateString()}</em>
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-white mb-4">
                1. Acceptance of Terms
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                By accessing and using The Super Tip ("the Service"), you accept and agree to be bound by the terms
                and provision of this agreement. If you do not agree to abide by the above, please do not use
                this service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-white mb-4">
                2. Use License
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                Permission is granted to temporarily use the Service for personal, non-commercial transitory
                viewing only. This is the grant of a license, not a transfer of title, and under this license you may not:
              </p>
              <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2">
                <li>Modify or copy the materials</li>
                <li>Use the materials for any commercial purpose or for any public display</li>
                <li>Attempt to decompile or reverse engineer any software contained on the Service</li>
                <li>Remove any copyright or other proprietary notations from the materials</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-white mb-4">
                3. Age Restriction
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                You must be 18 years of age or older to access our tips and prognostics. We reserve the right to verify your age
                and may restrict access if we believe you are under 18. We take all necessary measures to prevent access by minors.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-white mb-4">
                4. Responsible Use
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                We promote responsible use of our tips and prognostics. Our content is provided for informational purposes only
                and should not be used as the sole basis for decisions. We encourage users to make informed choices.
              </p>

            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-white mb-4">
                5. Account Registration and Security
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                When you create an account with us, you must provide accurate and complete information.
                You are responsible for:
              </p>
              <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2">
                <li>Maintaining the confidentiality of your password and account</li>
                <li>All activities that occur under your account</li>
                <li>Notifying us immediately of any unauthorized use of your account</li>
                <li>Ensuring that you exit from your account at the end of each session</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-white mb-4">
                6. Tips and Prognostics
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                All tips and prognostics provided on our platform are for informational purposes only. While we strive
                for accuracy, predictions involve uncertainty and there are no guaranteed outcomes. You acknowledge that:
              </p>
              <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2">
                <li>All decisions based on our tips are made at your own risk</li>
                <li>Predictions are subject to change based on new information</li>
                <li>We reserve the right to modify or remove tips at our discretion</li>
                <li>You are responsible for any actions taken based on our advice</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-white mb-4">
                7. Subscription and Payments
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                All subscriptions and payments must be made through approved methods. We are not responsible for any fees charged
                by third-party payment processors. You agree to:
              </p>
              <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2">
                <li>Provide accurate payment information</li>
                <li>Only use funds that you legally own</li>
                <li>Not attempt to reverse payments or chargebacks after subscription</li>
                <li>Settle all outstanding balances promptly</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-white mb-4">
                8. Prohibited Conduct
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                The following activities are strictly prohibited:
              </p>
              <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2">
                <li>Using the service for any illegal purpose</li>
                <li>Creating multiple accounts or using false information</li>
                <li>Engaging in fraudulent or manipulative behavior</li>
                <li>Harassing other users or our staff</li>
                <li>Attempting to hack, crack, or compromise our systems</li>
                <li>Sharing your account credentials with others</li>
                <li>Using automated tools or bots to access the service</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-white mb-4">
                9. Intellectual Property Rights
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                The Service and its original content, features, and functionality are owned by The Super Tip and are
                protected by copyright, trademark, and other intellectual property laws. You may not reproduce,
                distribute, modify, or create derivative works of our content without explicit permission.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-white mb-4">
                10. Limitation of Liability
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                In no event shall The Super Tip, nor its directors, employees, partners, agents, suppliers, or affiliates,
                be liable for any indirect, incidental, special, consequential, or punitive damages, including without
                limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your use
                of the service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-white mb-4">
                11. Termination
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                We may terminate or suspend your account immediately, without prior notice or liability, for any
                reason whatsoever, including without limitation if you breach the Terms. Upon termination, your
                right to use the Service will cease immediately.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-white mb-4">
                12. Governing Law
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                These Terms shall be interpreted and governed by the laws of the jurisdiction in which The Super Tip
                operates, without regard to its conflict of law provisions. You agree to submit to the non-exclusive
                jurisdiction of the courts located in that jurisdiction for the resolution of any disputes.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-white mb-4">
                13. Changes to Terms
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a
                revision is material, we will try to provide at least 30 days notice prior to any new terms taking effect.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-white mb-4">
                14. Contact Information
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                If you have any questions about these Terms of Service, please contact us:
              </p>
              <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded">
                <p className="text-gray-700 dark:text-gray-300">
                  <strong>Email:</strong> <a href="mailto:thesupertip@gmail.com" className="text-blue-600 dark:text-blue-400 hover:underline">thesupertip@gmail.com</a>
                </p>
                <p className="text-gray-700 dark:text-gray-300">
                  <strong>Subject:</strong> Terms of Service Inquiry
                </p>
              </div>
            </section>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default TermsOfService;
